import React, { useRef, useEffect, useState } from 'react'
import { bool, number, shape } from 'prop-types'

import classNames from 'classnames'

import StackableSurfaceItem from 'Renderer/StackableSurfaceItem'
import { stackableSurfaceItemType, renderItemType, furnitureItemType } from 'models/propTypes'
import Icon from 'components/uiElements/Icon'

import { getRenderer } from 'models/refStore/selectors'

require('./CompositionStackableSurfaceItem.scss')

const CompositionStackableSurfaceItem = ({
    parent,
    plane,
    visible,
    highlight,
    incomingFurnitureData,
    selectedRenderItem,
    mouseOverCoords,
}) => {
    const renderObjectRef = useRef(null)

    const defaultCorners = {
        left: 0,
        right: 0,
    }
    const [corners, setCorners] = useState(defaultCorners)

    const onGhostRender = ({ corners: itemCorners }) => {
        if (!itemCorners) {
            return
        }

        const left = itemCorners[0] * 100
        const right = itemCorners[1] * 100
        const top = itemCorners[2] * 100
        const bottom = itemCorners[3] * 100

        const minSizeX = 2.5 // %
        const minSizeY = 5 // %

        let paddingX = 0
        let paddingY = 0
        if (bottom - top < minSizeY) {
            paddingY = (minSizeY - (bottom - top)) / 2
        }
        if (right - left < minSizeX) {
            paddingX = (minSizeX - (right - left)) / 2
        }

        setCorners({
            top: Math.max(0, top - paddingY),
            bottom: Math.max(0, 100 - bottom - paddingY),
            left: Math.max(0, left - paddingX),
            right: Math.max(0, 100 - right - paddingX),
        })
    }

    useEffect(() => {
        const renderer = getRenderer()
        if (renderer) {
            renderObjectRef.current = new StackableSurfaceItem(plane.id, renderer, plane, parent, onGhostRender)
            renderObjectRef.current.setVisible(false)
        }
        return () => {
            if (renderObjectRef.current) {
                renderObjectRef.current.endIsNigh()
                renderObjectRef.current = null
            }
        }
    }, [parent && parent.uniqueId, plane && plane.id])

    useEffect(() => {
        if (!renderObjectRef.current) {
            return
        }
        renderObjectRef.current.setVisible(visible)
    }, [visible])

    useEffect(() => {
        if (!renderObjectRef.current) {
            return
        }
        renderObjectRef.current.setHighlighted(highlight)
    }, [highlight])

    useEffect(() => {
        if (!renderObjectRef.current) {
            return
        }
        if (highlight && visible) {
            renderObjectRef.current.showGhost(incomingFurnitureData || selectedRenderItem)
        } else {
            renderObjectRef.current.hideGhost()
        }
    }, [visible, highlight, incomingFurnitureData, selectedRenderItem])

    useEffect(() => {
        if (!renderObjectRef.current) {
            return
        }
        if (highlight && visible && mouseOverCoords !== null) {
            renderObjectRef.current.setGhostPosition([mouseOverCoords.x, mouseOverCoords.y])
        }
    }, [visible, highlight, mouseOverCoords])

    const boxStyle = {
        left: corners.left && `${corners.left}%`,
        bottom: corners.top && `${corners.top}%`,
        right: corners.right && `${corners.right}%`,
        top: corners.bottom && `${corners.bottom}%`,
    }

    const iconVisible = highlight && visible
    return (
        <div className={classNames('placementWrapper', { visible: iconVisible })} style={boxStyle}>
            <Icon className={classNames('placementIcon', { visible: iconVisible })} code="location_pointer" svg />
        </div>
    )
}

CompositionStackableSurfaceItem.defaultProps = {
    visible: false,
    highlight: false,
    parent: null,
    incomingFurnitureData: null,
    selectedRenderItem: null,
    mouseOverCoords: null,
}

CompositionStackableSurfaceItem.propTypes = {
    plane: stackableSurfaceItemType.isRequired,
    visible: bool,
    highlight: bool,
    parent: renderItemType,
    incomingFurnitureData: furnitureItemType,
    selectedRenderItem: renderItemType,
    mouseOverCoords: shape({ x: number, y: number }),
}

export default CompositionStackableSurfaceItem
